import React, { useState } from "react";
import {
  Container,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  Box,
  Alert,
  Button,
  ButtonGroup,
} from "@mui/material";
import { useGetBestScorersQuery } from "state/api";

const ScoreDashboard = ({ product = null, setSelectedProduct = null }) => {
  const { data: apiResponse = {}, isLoading } = useGetBestScorersQuery({
    product,
  });
  const [availableProducts, setAvailableProducts] = useState([
    "Queezy-ER",
    "Finease",
  ]);

  // Extract the actual data array from the response
  const data = Array.isArray(apiResponse)
    ? apiResponse
    : apiResponse?.data || [];

  console.log("API Data:", data);

  if (isLoading)
    return (
      <Container maxWidth="md" sx={{ mt: 3, mb: 5 }}>
        <Typography variant="h4" gutterBottom>
          Loading Top Scorers...
        </Typography>
      </Container>
    );

  // ✅ If specific product is selected, only show that product
  let filteredData = data;
  if (product) {
    filteredData = data.filter((item) => item.product === product);
  } else {
    // If no product selected, show all allowed products
    filteredData = data.filter(
      (item) => item.product === "Queezy-ER" || item.product === "Finease"
    );
  }

  // ✅ Group the filtered data by product (if not filtered by single product)
  const groupedByProduct = product
    ? { [product]: filteredData }
    : filteredData.reduce((acc, curr) => {
        const { product: prod } = curr;
        if (!acc[prod]) acc[prod] = [];
        acc[prod].push(curr);
        return acc;
      }, {});

  const results = Object.entries(groupedByProduct).map(([product, records]) => {
    // ✅ Top 5 MRs per product
    const mrScores = records.reduce((acc, curr) => {
      const key = `${curr.EmpNo}-${product}`;
      if (!acc[key]) {
        acc[key] = {
          MrEmpNo: curr.EmpNo,
          MrName: curr.MrName,
          HeadQrt: curr.HeadQrt,
          score: 0,
          product,
        };
      }
      acc[key].score += Number(curr.score) || 0;
      return acc;
    }, {});

    const top5MRs = Object.values(mrScores)
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);

    // ✅ Top 3 RMs per product (based on average score)
    const rmScores = records.reduce((acc, curr) => {
      const key = `${curr.RmMgName}-${product}`;
      if (!acc[key]) {
        acc[key] = {
          RmMgName: curr.RmMgName,
          HeadQrt: curr.HeadQrt,
          totalScore: 0,
          recordCount: 0,
          product,
        };
      }
      acc[key].totalScore += Number(curr.score) || 0;
      acc[key].recordCount += 1;
      return acc;
    }, {});

    const top3RMs = Object.values(rmScores)
      .sort((a, b) => b.totalScore - a.totalScore)
      .slice(0, 3);

    return { product, top5MRs, top3RMs };
  });

  return (
    <Container maxWidth="md" sx={{ mt: 3, mb: 5 }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{ color: "white", textAlign: "center", mb: 3 }}
      >
        ⭐ Top Scorers (Daily Basis)
      </Typography>

      {/* Product Filter Buttons - Only show if no product is selected */}
      {!product && setSelectedProduct && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 1,
            mb: 3,
            flexWrap: "wrap",
          }}
        >
          <Button
            variant={product === null ? "contained" : "outlined"}
            onClick={() => setSelectedProduct(null)}
            sx={{
              backgroundColor: product === null ? "#3b82f6" : "transparent",
              borderColor: "#3b82f6",
              color: product === null ? "white" : "#3b82f6",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#3b82f6",
                color: "white",
              },
            }}
          >
            All Products
          </Button>
          {availableProducts.map((prod) => (
            <Button
              key={prod}
              variant={product === prod ? "contained" : "outlined"}
              onClick={() => setSelectedProduct(prod)}
              sx={{
                backgroundColor: product === prod ? "#3b82f6" : "transparent",
                borderColor: "#3b82f6",
                color: product === prod ? "white" : "#3b82f6",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#3b82f6",
                  color: "white",
                },
              }}
            >
              {prod}
            </Button>
          ))}
        </Box>
      )}

      {results.length === 0 && (
        <Alert severity="info">
          No scores available for the current period.
        </Alert>
      )}

      {results.map(({ product, top5MRs, top3RMs }) => (
        <Box key={product} sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            sx={{
              mb: 2,
              color: "gold",
              textAlign: "center",
              textTransform: "uppercase",
              letterSpacing: 1,
            }}
          >
            {product}
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 3,
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            {/* Top 5 MRs */}
            <Paper
              sx={{
                flex: 1,
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(25px)",
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  p: 2,
                  backgroundColor: "rgba(63, 81, 181, 0.8)",
                  color: "white",
                  textAlign: "center",
                  borderRadius: "8px 8px 0 0",
                }}
              >
                Top 5 Medical Representatives
              </Typography>
              <List sx={{ minWidth: 280 }}>
                {top5MRs.length > 0 ? (
                  top5MRs.map((mr, idx) => (
                    <ListItem
                      key={`${mr.MrEmpNo}-${product}`}
                      disablePadding
                      sx={{
                        borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
                        "&:hover": {
                          backgroundColor: "rgba(63, 81, 181, 0.1)",
                        },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              width: "100%",
                              p: 2,
                            }}
                          >
                            <Box>
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: "bold",
                                  color: "white",
                                }}
                              >
                                #{idx + 1} {mr.MrName}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: "lightgray" }}
                              >
                                {mr.MrEmpNo} | {mr.HeadQrt}
                              </Typography>
                            </Box>
                            <Typography variant="h6" sx={{ color: "gold" }}>
                              {mr.score} pts
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))
                ) : (
                  <ListItem>
                    <ListItemText
                      primary="No data available"
                      sx={{ color: "white", textAlign: "center" }}
                    />
                  </ListItem>
                )}
              </List>
            </Paper>

            {/* Top 3 RMs */}
            <Paper
              sx={{
                flex: 1,
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(25px)",
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  p: 2,
                  backgroundColor: "rgba(56, 142, 60, 0.8)",
                  color: "white",
                  textAlign: "center",
                  borderRadius: "8px 8px 0 0",
                }}
              >
                Top 3 Regional Managers
              </Typography>
              <List sx={{ minWidth: 280 }}>
                {top3RMs.length > 0 ? (
                  top3RMs.map((rm, idx) => (
                    <ListItem
                      key={`${rm.RmMgName}-${product}`}
                      disablePadding
                      sx={{
                        borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
                        "&:hover": {
                          backgroundColor: "rgba(56, 142, 60, 0.1)",
                        },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              width: "100%",
                              p: 2,
                            }}
                          >
                            <Box>
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: "bold",
                                  color: "white",
                                }}
                              >
                                #{idx + 1} {rm.RmMgName}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: "lightgray" }}
                              >
                                {rm.HeadQrt}
                              </Typography>
                            </Box>
                            <Typography variant="h6" sx={{ color: "gold" }}>
                              {rm.totalScore} pts
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))
                ) : (
                  <ListItem>
                    <ListItemText
                      primary="No data available"
                      sx={{ color: "white", textAlign: "center" }}
                    />
                  </ListItem>
                )}
              </List>
            </Paper>
          </Box>
        </Box>
      ))}
    </Container>
  );
};

export default ScoreDashboard;
